import { RefObject } from "react";
import EmptyStateGreeting from "./EmptyStateGreeting";
import EmptyStateFeatureCards from "./EmptyStateFeatureCards";
import ChatInput from "@/pages/chat/components/ChatInput";
import Workspace from "@/models/workspace/Workspace";
import { useContent } from "@/hooks/useContent";

type Props = {
  workspaces: Workspace[];
  isLoadingWorkspaces: boolean;
  onPromptSelect?: (promptText: string) => void;
  onSubmit: (text: string) => void;
  disabled: boolean;
  setInputValueRef?: RefObject<((value: string) => void) | null>;
  /** True while the PII NER model is still loading (LAR-27 v2, decision 5). */
  piiPreparing?: boolean;
  /** True while a PII scan runs — spinner on the send button (LAR-27 v2). */
  piiScanning?: boolean;
};

const ChatEmptyState: React.FC<Props> = ({
  workspaces,
  isLoadingWorkspaces,
  onPromptSelect,
  onSubmit,
  disabled,
  setInputValueRef,
  piiPreparing,
  piiScanning,
}) => {
  const { t } = useContent();

  return (
    <div className="flex flex-col items-center justify-center min-h-full w-full px-4 py-8 gap-8 max-w-4xl mx-auto">
      {/* Greeting Section */}
      <EmptyStateGreeting />

      {/* Feature Cards Section */}
      <EmptyStateFeatureCards
        workspaces={workspaces}
        isLoadingWorkspaces={isLoadingWorkspaces}
        onPromptSelect={onPromptSelect}
      />

      {/* Chat Input Section */}
      <div className="flex flex-col justify-center items-center w-full ">
        {piiPreparing && (
          <span className="w-full pb-1 text-xs text-muted-foreground animate-pulse">
            Forbereder sikkerhedstjek…
          </span>
        )}
        <ChatInput
          onSubmit={onSubmit}
          isScanning={piiScanning}
          inputTestId="chat-input"
          submitTestId="chat-send-button"
          disabled={disabled}
          selectedConversation={undefined}
          setInputValueRef={setInputValueRef}
          workspaces={workspaces}
          isLoadingWorkspaces={isLoadingWorkspaces}
          // Always defer clearing the input to the send-proceeds path, so a
          // cancelled PII review (or the no-context warning) keeps the prompt
          // (LAR-27 v2 UI #1). handleFirstMessageWithNoContext still shows the
          // no-context warning when appropriate.
          onPendingSubmit={() => true}
        />
        <span className="text-xs text-foreground/80 py-1 mt-2">
          {t("chat.disclaimer")}
        </span>
      </div>
    </div>
  );
};

export default ChatEmptyState;
